import React, { useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { FaCheckCircle, FaPlug, FaSyncAlt, FaTruck, FaMoneyCheckAlt } from "react-icons/fa";
import "../product/AdminProducts.css";
import "./CourierSettings.css";

/**
 * Settings → Couriers. PostEx and Leopards connect through their APIs:
 * book from the order page, tracking updates on its own, and PostEx payouts
 * are recorded automatically. For other couriers (and Leopards payouts),
 * paste the tracking numbers from the courier's payment statement.
 */
export default function CourierSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ postex: {}, leopards: {}, default_weight: 500 });
  const [pickupAddresses, setPickupAddresses] = useState([]);
  const [leopardsCities, setLeopardsCities] = useState([]);
  const [busy, setBusy] = useState("");
  const [payout, setPayout] = useState({ tracking_numbers: "", paid_on: new Date().toISOString().slice(0, 10) });
  const [payoutResult, setPayoutResult] = useState(null);

  const load = () => {
    axios.get("/api/admin/couriers").then((res) => {
      if (res.data.status !== 200) return;
      const s = res.data.settings;
      setSettings(s);
      setForm({
        postex: { enabled: s.postex.enabled, token: "", pickup_code: s.postex.pickup_code },
        leopards: { enabled: s.leopards.enabled, key: "", password: "", origin_city: s.leopards.origin_city },
        default_weight: s.default_weight,
      });
    });
  };

  useEffect(() => {
    document.title = "Couriers — Admin";
    load();
  }, []);

  const setCourier = (code, key, value) => setForm((current) => ({ ...current, [code]: { ...current[code], [key]: value } }));

  const save = () => {
    setBusy("save");
    return axios.put("/api/admin/couriers", form)
      .then((res) => {
        if (res.data.status === 200) {
          swal("Saved", res.data.message, "success");
          load();
        }
      })
      .catch((err) => swal("Not saved", err.response?.data?.message || "Please check the fields.", "error"))
      .finally(() => setBusy(""));
  };

  const test = (code) => {
    setBusy(`test-${code}`);
    // Save first, so the test uses the keys just typed.
    axios.put("/api/admin/couriers", form)
      .then(() => axios.post(`/api/admin/couriers/${code}/test`))
      .then((res) => {
        if (res.data.status === 200) {
          if (res.data.pickup_addresses) setPickupAddresses(res.data.pickup_addresses);
          if (res.data.cities) setLeopardsCities(res.data.cities);
          swal("Connected", res.data.message, "success");
          load();
        } else {
          swal("Not connected", res.data.message, "error");
        }
      })
      .catch(() => swal("Not connected", "Could not test the connection.", "error"))
      .finally(() => setBusy(""));
  };

  const syncNow = () => {
    setBusy("sync");
    axios.post("/api/admin/couriers/sync")
      .then((res) => swal("Tracking updated", res.data.message, "success"))
      .catch(() => swal("Not updated", "Please try again.", "error"))
      .finally(() => { setBusy(""); load(); });
  };

  const recordPayouts = (event) => {
    event.preventDefault();
    setBusy("payout");
    setPayoutResult(null);
    axios.post("/api/admin/couriers/payouts", payout)
      .then((res) => {
        if (res.data.status === 200) {
          setPayoutResult(res.data.result);
          if (res.data.result.recorded.length) setPayout((current) => ({ ...current, tracking_numbers: "" }));
        }
      })
      .catch((err) => swal("Not recorded", err.response?.data?.message || "Please check the tracking numbers.", "error"))
      .finally(() => setBusy(""));
  };

  if (!settings) {
    return <div className="ap"><div className="p-5 text-center"><div className="spinner-border text-primary" role="status" /></div></div>;
  }

  return (
    <div className="ap cs">
      <div className="ap-head">
        <div>
          <h4>Couriers</h4>
          <p>Book parcels from the order page, print the airway bill, and let tracking and courier payouts update by themselves.</p>
        </div>
        <div className="ap-head-actions">
          <button type="button" className="ap-btn" onClick={syncNow} disabled={busy === "sync"}>
            <FaSyncAlt size={11} /> {busy === "sync" ? "Updating…" : "Update tracking now"}
          </button>
        </div>
      </div>

      <div className="cs-how">
        <div><span>1</span><strong>Connect</strong> a courier below with the API keys from its merchant portal.</div>
        <div><span>2</span><strong>Book</strong> from any order: Orders → open an order → "Book courier". The airway bill is ready to print.</div>
        <div><span>3</span><strong>Relax.</strong> Every 30 minutes tracking updates. Delivered orders are marked delivered, returned parcels cancel the order, and courier payouts move the money to Collected on the Money page.</div>
      </div>
      {settings.last_sync && <p className="text-muted small mb-3">Last tracking update: {new Date(settings.last_sync.replace(" ", "T")).toLocaleString()}</p>}

      <div className="cs-grid">
        {/* PostEx */}
        <section className="ap-card cs-card">
          <div className="cs-card-head">
            <span className="cs-logo is-postex">PX</span>
            <div className="flex-grow-1">
              <h5>PostEx</h5>
              <small>Booking, airway bill, tracking and payouts are all automatic.</small>
            </div>
            <label className="cs-switch">
              <input type="checkbox" checked={!!form.postex.enabled} onChange={(e) => setCourier("postex", "enabled", e.target.checked)} /> On
            </label>
          </div>
          <label className="form-label small fw-semibold">API token {settings.postex.has_token && <span className="cs-saved"><FaCheckCircle size={10} /> saved</span>}</label>
          <input type="password" className="form-control form-control-sm mb-2" autoComplete="off"
            placeholder={settings.postex.has_token ? "Leave empty to keep the saved token" : "From the PostEx merchant portal"}
            value={form.postex.token || ""} onChange={(e) => setCourier("postex", "token", e.target.value)} />
          <label className="form-label small fw-semibold">Pickup address</label>
          {pickupAddresses.length ? (
            <select className="form-select form-select-sm mb-2" value={form.postex.pickup_code || ""} onChange={(e) => setCourier("postex", "pickup_code", e.target.value)}>
              <option value="">Choose a pickup address</option>
              {pickupAddresses.map((a) => <option key={a.code} value={a.code}>{a.city} · {a.address}</option>)}
            </select>
          ) : (
            <input className="form-control form-control-sm mb-2" placeholder="Press Test connection to choose" value={form.postex.pickup_code || ""}
              onChange={(e) => setCourier("postex", "pickup_code", e.target.value)} />
          )}
          <div className="cs-actions">
            <button type="button" className="ap-btn" onClick={() => test("postex")} disabled={!!busy}>
              <FaPlug size={11} /> {busy === "test-postex" ? "Testing…" : "Test connection"}
            </button>
          </div>
        </section>

        {/* Leopards */}
        <section className="ap-card cs-card">
          <div className="cs-card-head">
            <span className="cs-logo is-leopards">LCS</span>
            <div className="flex-grow-1">
              <h5>Leopards Courier</h5>
              <small>Booking, airway bill and tracking are automatic. Record payouts from the LCS payment statement below.</small>
            </div>
            <label className="cs-switch">
              <input type="checkbox" checked={!!form.leopards.enabled} onChange={(e) => setCourier("leopards", "enabled", e.target.checked)} /> On
            </label>
          </div>
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label small fw-semibold">API key {settings.leopards.has_key && <span className="cs-saved"><FaCheckCircle size={10} /> saved</span>}</label>
              <input type="password" className="form-control form-control-sm mb-2" autoComplete="off" placeholder={settings.leopards.has_key ? "Keep saved" : "API key"}
                value={form.leopards.key || ""} onChange={(e) => setCourier("leopards", "key", e.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label small fw-semibold">API password</label>
              <input type="password" className="form-control form-control-sm mb-2" autoComplete="off" placeholder={settings.leopards.has_key ? "Keep saved" : "API password"}
                value={form.leopards.password || ""} onChange={(e) => setCourier("leopards", "password", e.target.value)} />
            </div>
          </div>
          <label className="form-label small fw-semibold">Pickup city</label>
          {leopardsCities.length ? (
            <select className="form-select form-select-sm mb-2" value={form.leopards.origin_city || ""} onChange={(e) => setCourier("leopards", "origin_city", e.target.value)}>
              <option value="">Choose your city</option>
              {leopardsCities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : (
            <input className="form-control form-control-sm mb-2" placeholder="Press Test connection to choose" value={form.leopards.origin_city || ""}
              onChange={(e) => setCourier("leopards", "origin_city", e.target.value)} />
          )}
          <div className="cs-actions">
            <button type="button" className="ap-btn" onClick={() => test("leopards")} disabled={!!busy}>
              <FaPlug size={11} /> {busy === "test-leopards" ? "Testing…" : "Test connection"}
            </button>
          </div>
        </section>

        {/* Couriers without an API connection */}
        <section className="ap-card cs-card">
          <div className="cs-card-head">
            <span className="cs-logo"><FaTruck size={14} /></span>
            <div className="flex-grow-1">
              <h5>Trax, TCS, M&amp;P and others</h5>
              <small>Book on the courier's own website, then on the order press "Fulfill &amp; Ship" and enter its tracking number. Record payouts below.</small>
            </div>
          </div>
          <label className="form-label small fw-semibold">Parcel weight per item (grams)</label>
          <input type="number" min="1" className="form-control form-control-sm mb-2" style={{ maxWidth: 160 }}
            value={form.default_weight} onChange={(e) => setForm((c) => ({ ...c, default_weight: e.target.value }))} />
          <small className="text-muted">Used to fill in the weight when booking. You can change it on each booking.</small>
        </section>
      </div>

      <div className="d-flex justify-content-end mb-4">
        <button type="button" className="ap-btn ap-btn-primary" onClick={save} disabled={!!busy}>{busy === "save" ? "Saving…" : "Save courier settings"}</button>
      </div>

      {/* Payouts from a statement */}
      <form className="ap-card cs-card" onSubmit={recordPayouts}>
        <div className="cs-card-head">
          <span className="cs-logo is-money"><FaMoneyCheckAlt size={14} /></span>
          <div className="flex-grow-1">
            <h5>Record a courier payout</h5>
            <small>When a courier pays you (bank transfer or cheque), paste the tracking numbers from its payment statement. Those orders move from "Still to collect" to Collected (bank) on the Money page. PostEx payouts are recorded automatically.</small>
          </div>
        </div>
        <div className="row g-2 align-items-end">
          <div className="col-md-8">
            <label className="form-label small fw-semibold">Tracking numbers (one per line, or separated by commas)</label>
            <textarea className="form-control form-control-sm" rows={4} value={payout.tracking_numbers}
              onChange={(e) => setPayout((c) => ({ ...c, tracking_numbers: e.target.value }))} placeholder={"KI7501234567\nKI7501234568"} required />
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-semibold">Paid on</label>
            <input type="date" className="form-control form-control-sm mb-2" value={payout.paid_on} onChange={(e) => setPayout((c) => ({ ...c, paid_on: e.target.value }))} />
            <button type="submit" className="ap-btn ap-btn-primary w-100" disabled={busy === "payout"}>{busy === "payout" ? "Recording…" : "Record payout"}</button>
          </div>
        </div>
        {payoutResult && (
          <div className="cs-result">
            {payoutResult.recorded.length > 0 && <div className="is-ok">Recorded {payoutResult.recorded.length}: {payoutResult.recorded.join(", ")}</div>}
            {payoutResult.already.length > 0 && <div>Already paid: {payoutResult.already.join(", ")}</div>}
            {payoutResult.not_found.length > 0 && <div className="is-warn">Not found on any order: {payoutResult.not_found.join(", ")}</div>}
            {payoutResult.failed.length > 0 && <div className="is-warn">Not recorded: {payoutResult.failed.join("; ")}</div>}
          </div>
        )}
      </form>
    </div>
  );
}
